import React, { useMemo } from "react";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from "chart.js";

import { Bar, Doughnut } from "react-chartjs-2";


/*
|--------------------------------------------------------------------------
| Register Chart.js Components
|--------------------------------------------------------------------------
*/

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);


/*
|--------------------------------------------------------------------------
| PartyCharts Component
|--------------------------------------------------------------------------
*/

function PartyCharts({ partyData = {}, partyTypes = [] }) {


    /*
    |--------------------------------------------------------------------------
    | Prepare Chart Data
    |--------------------------------------------------------------------------
    */

    const chartData = useMemo(() => {

        const labels = partyTypes.map(
            (party) => party.title
        );


        /*
        |--------------------------------------------------------------------------
        | Party Counts
        |--------------------------------------------------------------------------
        */

        const partyCounts = partyTypes.map(
            (partyType) =>
                (partyData[partyType.key] || []).length
        );


        /*
        |--------------------------------------------------------------------------
        | Opening Balances
        |--------------------------------------------------------------------------
        */

        const openingBalances = partyTypes.map(
            (partyType) => {

                return (partyData[partyType.key] || [])
                    .reduce(
                        (total, party) =>
                            total +
                            Number(
                                party.opening_balance || 0
                            ),
                        0
                    );

            }
        );


        /*
        |--------------------------------------------------------------------------
        | Ledger Amounts
        |--------------------------------------------------------------------------
        */

        const ledgerAmounts = partyTypes.map(
            (partyType) => {

                return (partyData[partyType.key] || [])
                    .reduce(
                        (total, party) =>
                            total +
                            Number(
                                party.total_amount || 0
                            ),
                        0
                    );

            }
        );


        /*
        |--------------------------------------------------------------------------
        | Current Balances
        |--------------------------------------------------------------------------
        */

        const currentBalances = partyTypes.map(
            (partyType) => {

                return (partyData[partyType.key] || [])
                    .reduce(
                        (total, party) => {

                            const openingBalance =
                                Number(
                                    party.opening_balance || 0
                                );

                            const ledgerAmount =
                                Number(
                                    party.total_amount || 0
                                );

                            const currentBalance =
                                Number(
                                    party.current_balance ??
                                    (
                                        openingBalance +
                                        ledgerAmount
                                    )
                                );

                            return total + currentBalance;

                        },
                        0
                    );

            }
        );


        /*
        |--------------------------------------------------------------------------
        | Transaction Counts
        |--------------------------------------------------------------------------
        */

        const transactionCounts = partyTypes.map(
            (partyType) => {

                return (partyData[partyType.key] || [])
                    .reduce(
                        (total, party) =>
                            total +
                            Number(
                                party.ledger_count || 0
                            ),
                        0
                    );

            }
        );


        return {
            labels,
            partyCounts,
            openingBalances,
            ledgerAmounts,
            currentBalances,
            transactionCounts
        };

    }, [partyData, partyTypes]);


    /*
    |--------------------------------------------------------------------------
    | Financial Chart
    |--------------------------------------------------------------------------
    */

    const financialChartData = {

    labels: chartData.labels,

    datasets: [

        {
            label: "Opening Balance",

            data: chartData.openingBalances,

            backgroundColor: "rgba(54, 162, 235, 0.75)",
            borderColor: "rgba(54, 162, 235, 1)",

            borderWidth: 1
        },

        {
            label: "Ledger Amount",

            data: chartData.ledgerAmounts,

            backgroundColor: "rgba(40, 167, 69, 0.75)",
            borderColor: "rgba(40, 167, 69, 1)",

            borderWidth: 1
        },

        {
            label: "Current Balance",

            data: chartData.currentBalances,

            backgroundColor: "rgba(255, 159, 64, 0.75)",
            borderColor: "rgba(255, 159, 64, 1)",

            borderWidth: 1
        }

    ]

};


    /*
    |--------------------------------------------------------------------------
    | Party Distribution Chart
    |--------------------------------------------------------------------------
    */

    const partyCountChartData = {

    labels: chartData.labels,

    datasets: [

        {
            label: "Party Count",

            data: chartData.partyCounts,

            backgroundColor: [
                "rgba(54, 162, 235, 0.8)",
                "rgba(40, 167, 69, 0.8)",
                "rgba(255, 193, 7, 0.8)",
                "rgba(220, 53, 69, 0.8)",
                "rgba(111, 66, 193, 0.8)",
                "rgba(23, 162, 184, 0.8)",
                "rgba(253, 126, 20, 0.8)",
                "rgba(108, 117, 125, 0.8)",
                "rgba(32, 201, 151, 0.8)"
            ],

            borderColor: "#ffffff",

            borderWidth: 2,

            hoverOffset: 8

        }

    ]

};


    /*
    |--------------------------------------------------------------------------
    | Transaction Chart
    |--------------------------------------------------------------------------
    */

    const transactionChartData = {

        labels: chartData.labels,

        datasets: [

            {
                label: "Transactions",

                data: chartData.transactionCounts,

                borderWidth: 1
            }

        ]

    };


    /*
    |--------------------------------------------------------------------------
    | Currency Formatter
    |--------------------------------------------------------------------------
    */

    const formatCurrency = (value) => {

        return (
            "₹" +
            Number(value || 0).toLocaleString(
                "en-IN",
                {
                    maximumFractionDigits: 2
                }
            )
        );

    };


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (

        <>

            {/* =====================================================
                FINANCIAL SUMMARY + PARTY DISTRIBUTION
            ====================================================== */}

            <div className="row g-4 mb-4">


                {/* =================================================
                    FINANCIAL SUMMARY
                ================================================== */}

                <div className="col-xl-8">

                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-header bg-white py-3">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <h5 className="fw-bold mb-1">

                                        Party Financial Summary

                                    </h5>

                                    <small className="text-muted">

                                        Opening balance, ledger amount
                                        and current balance

                                    </small>

                                </div>

                                <i className="bi bi-bar-chart-fill text-primary fs-4"></i>

                            </div>

                        </div>


                        <div className="card-body">

                            <div
                                style={{
                                    height: "350px"
                                }}
                            >

                                <Bar
                                    data={financialChartData}

                                    options={{

                                        responsive: true,

                                        maintainAspectRatio: false,

                                        plugins: {

                                            legend: {
                                                position: "top"
                                            },

                                            tooltip: {

                                                callbacks: {

                                                    label:
                                                        function (context) {

                                                            return (
                                                                context.dataset.label +
                                                                ": " +
                                                                formatCurrency(
                                                                    context.raw
                                                                )
                                                            );

                                                        }

                                                }

                                            }

                                        },

                                        scales: {

                                            y: {

                                                beginAtZero: true,

                                                ticks: {

                                                    callback:
                                                        function (value) {

                                                            return formatCurrency(
                                                                value
                                                            );

                                                        }

                                                }

                                            }

                                        }

                                    }}
                                />

                            </div>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    PARTY DISTRIBUTION
                ================================================== */}

                <div className="col-xl-4">

                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-header bg-white py-3">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <h5 className="fw-bold mb-1">

                                        Parties Distribution

                                    </h5>

                                    <small className="text-muted">

                                        Number of parties by category

                                    </small>

                                </div>

                                <i className="bi bi-pie-chart-fill text-primary fs-4"></i>

                            </div>

                        </div>


                        <div className="card-body">

                            <div
                                style={{
                                    height: "350px",
                                    display: "flex",
                                    justifyContent: "center"
                                }}
                            >

                                <Doughnut

                                    data={partyCountChartData}

                                    options={{

                                        responsive: true,

                                        maintainAspectRatio: false,

                                        plugins: {

                                            legend: {
                                                position: "bottom"
                                            },

                                            tooltip: {

                                                callbacks: {

                                                    label:
                                                        function (context) {

                                                            const value =
                                                                context.raw || 0;

                                                            return (
                                                                context.label +
                                                                ": " +
                                                                value +
                                                                " parties"
                                                            );

                                                        }

                                                }

                                            }

                                        }

                                    }}

                                />

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* =====================================================
                TRANSACTIONS CHART
            ====================================================== */}

            <div className="row g-4 mb-4">

                <div className="col-12">

                    <div className="card border-0 shadow-sm">


                        <div className="card-header bg-white py-3">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <h5 className="fw-bold mb-1">

                                        Transactions by Party Type

                                    </h5>

                                    <small className="text-muted">

                                        Total ledger transactions recorded
                                        for each party category

                                    </small>

                                </div>

                                <i className="bi bi-graph-up-arrow text-primary fs-4"></i>

                            </div>

                        </div>


                        <div className="card-body">

                            <div
                                style={{
                                    height: "300px"
                                }}
                            >

                                <Bar

                                    data={transactionChartData}

                                    options={{

                                        responsive: true,

                                        maintainAspectRatio: false,

                                        plugins: {

                                            legend: {
                                                display: false
                                            },

                                            tooltip: {

                                                callbacks: {

                                                    label:
                                                        function (context) {

                                                            return (
                                                                "Transactions: " +
                                                                Number(
                                                                    context.raw || 0
                                                                ).toLocaleString(
                                                                    "en-IN"
                                                                )
                                                            );

                                                        }

                                                }

                                            }

                                        },

                                        scales: {

                                            y: {

                                                beginAtZero: true,

                                                ticks: {
                                                    precision: 0
                                                }

                                            }

                                        }

                                    }}

                                />

                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </>

    );

}


export default PartyCharts;
